import { Car, Camera, Eye, ArrowRight, CheckCircle, UploadSimple as Upload, Sparkle as Sparkles, Truck, Motorcycle as Bike, Crane as Construction, Boat as Ship } from '@phosphor-icons/react';
import { Helmet } from "react-helmet-async";
import { SEOHreflang } from "@/components/seo/SEOHreflang";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

import { useState, useMemo } from "react";
import { Link } from "@/lib/router-compat";
import { useToast } from "@/hooks/use-toast";
import { DemoImageUpload } from "@/components/listings/forms/DemoImageUpload";
import { useCountry } from "@/hooks/useCountry";
import { useTranslationV2 } from "@/hooks/useTranslationV2";
import { useCanonicalUrl } from "@/hooks/useCanonicalUrl";

const DemoGratuit = () => {
  const { toast } = useToast();
  const { country, config } = useCountry();
  const { t, isLoading } = useTranslationV2('demo');
  const canonicalUrl = useCanonicalUrl();
  
  // Use static keys initially, will update after translations load
  const [formData, setFormData] = useState({
    vehicleType: "cars",
    make: "Audi",
    model: "A4",
    year: "2019",
    mileage: "85000",
    fuel: "petrol",
    transmission: "automatic",
    price: "18500",
    condition: "very_good",
    location: country === 'UK' ? "London" : country === 'DE' ? "Berlin" : "București",
    description: "",
    contact: country === 'UK' ? "07123 456789" : country === 'DE' ? "0172 3456789" : "0712345678",
    email: "demo@example.com",
    images: [] as string[]
  });

  const preview = useMemo(() => {
    const formatter = new Intl.NumberFormat(config.locale);
    const priceFormatter = new Intl.NumberFormat(config.locale, { 
      style: 'currency', 
      currency: config.currency 
    });
    
    return {
      title: `${formData.make} ${formData.model} ${formData.year}`,
      subtitle: `${formatter.format(parseInt(formData.mileage) || 0)} ${config.distanceUnit} • ${t(`fuel_types.${formData.fuel}`)} • ${t(`transmissions.${formData.transmission}`)}`,
      price: priceFormatter.format(parseInt(formData.price) || 0),
      description: formData.description,
      location: formData.location,
      condition: t(`conditions.${formData.condition}`)
    };
  }, [formData, config, t]);

  const vehicleTypes = ["cars", "trucks", "motorcycles", "equipment", "boats"];

  const makesByType: Record<string, string[]> = {
    "cars": ["Audi", "BMW", "Mercedes-Benz", "Volkswagen", "Dacia", "Ford", "Toyota", "Hyundai", "Kia", "Nissan", "Honda", "Mazda", "Peugeot", "Renault", "Skoda"],
    "trucks": ["MAN", "Mercedes-Benz", "Scania", "Volvo", "DAF", "Iveco", "Renault Trucks", "Ford Trucks"],
    "motorcycles": ["Honda", "Yamaha", "Kawasaki", "Suzuki", "BMW", "Ducati", "Harley-Davidson", "KTM", "Triumph"],
    "equipment": ["Caterpillar", "Komatsu", "JCB", "Volvo", "Hitachi", "Liebherr", "Case", "New Holland", "John Deere"],
    "boats": ["Yamaha", "Sea-Doo", "Bayliner", "Quicksilver", "Jeanneau", "Bavaria", "Beneteau", "Princess"]
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2000 + 2 }, (_, i) => currentYear + 1 - i);

  const fuelTypes = ["petrol", "diesel", "lpg", "hybrid", "electric", "petrol_lpg"];
  
  const transmissions = ["manual", "automatic", "cvt", "automated"];
  
  const conditions = ["new", "very_good", "good", "satisfactory"];
  
  const locationsByCountry: Record<string, string[]> = {
    'RO': [
      "Alba Iulia", "Arad", "Bacău", "Baia Mare", "Bistrița", "Botoșani", 
      "Brăila", "Brașov", "București", "Buzău", "Cluj-Napoca", "Constanța", 
      "Craiova", "Galați", "Iași", "Oradea", "Pitești", "Ploiești", 
      "Sibiu", "Timișoara", "Târgu Mureș"
    ],
    'UK': [
      "London", "Manchester", "Birmingham", "Leeds", "Glasgow", "Liverpool",
      "Newcastle", "Sheffield", "Bristol", "Edinburgh", "Leicester", "Coventry"
    ],
    'DE': [
      "Berlin", "Hamburg", "München", "Köln", "Frankfurt", "Stuttgart",
      "Düsseldorf", "Dortmund", "Essen", "Leipzig", "Bremen", "Dresden"
    ]
  };

  const locations = locationsByCountry[country] || locationsByCountry['RO'];

  const vehicleIcons = {
    "cars": Car,
    "trucks": Truck,
    "motorcycles": Bike,
    "equipment": Construction,
    "boats": Ship
  };

  const VehicleIcon = vehicleIcons[formData.vehicleType as keyof typeof vehicleIcons] || Car;
  const mileageLabel = formData.vehicleType === "equipment" ? t('label_hours') : 
                       formData.vehicleType === "boats" ? t('label_engine_hours') : t('label_mileage');

  const handleDemoSubmit = () => {
    toast({
      title: t('toast_title'),
      description: t('toast_description'),
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHreflang canonicalUrl={canonicalUrl} />
      <Helmet>
        <title>{t('meta_title')}</title>
        <meta name="description" content={t('meta_description')} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t('meta_title')} />
        <meta property="og:description" content={t('meta_description')} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://roautomarket.ro/og-roautomarket.png" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@roautomarket" />
        <meta name="twitter:title" content={t('meta_title')} />
        <meta name="twitter:description" content={t('meta_description')} />
        <meta name="twitter:image" content="https://roautomarket.ro/og-roautomarket.png" />
      </Helmet>

      <main className="container mx-auto px-4 py-10 md:py-14">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Sparkles className="text-primary" size={28} />
              <Badge variant="secondary" className="text-sm px-3 py-1">
                {t('badge_free')}
              </Badge>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent mb-4">
              {t('title')}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('subtitle')}
            </p>
          </div>

          {/* Benefits Banner */}
          <Card className="mb-8 bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
            <CardContent className="p-6">
              <div className="grid md:grid-cols-3 gap-4 text-center">
                <div className="flex items-center gap-2 justify-center">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="text-sm font-medium">{t('benefit_no_registration')}</span>
                </div>
                <div className="flex items-center gap-2 justify-center">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="text-sm font-medium">{t('benefit_realtime_preview')}</span>
                </div>
                <div className="flex items-center gap-2 justify-center">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="text-sm font-medium">{t('benefit_auto_optimization')}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Form Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <VehicleIcon className="text-primary" />
                  {t('form_title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="vehicleType">{t('label_vehicle_type')}</Label>
                    <Select value={formData.vehicleType} onValueChange={(value) => setFormData({...formData, vehicleType: value, make: makesByType[value][0]})}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('label_vehicle_type')} />
                      </SelectTrigger>
                      <SelectContent>
                        {vehicleTypes.map((type) => (
                          <SelectItem key={type} value={type}>{t(`vehicle_types.${type}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="make">{t('label_make')}</Label>
                    <Select value={formData.make} onValueChange={(value) => setFormData({...formData, make: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('label_make')} />
                      </SelectTrigger>
                      <SelectContent>
                        {makesByType[formData.vehicleType].map((make) => (
                          <SelectItem key={make} value={make}>{make}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="model">{t('label_model')}</Label>
                    <Input
                      id="model"
                      placeholder={t('placeholder_model')}
                      value={formData.model}
                      onChange={(e) => setFormData({...formData, model: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="year">{t('label_year')}</Label>
                    <Select value={formData.year} onValueChange={(value) => setFormData({...formData, year: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('label_year')} />
                      </SelectTrigger>
                      <SelectContent>
                        {years.map((year) => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mileage">{mileageLabel}</Label>
                    <Input
                      id="mileage"
                      type="number"
                      placeholder={formData.vehicleType === "equipment" || formData.vehicleType === "boats" ? t('placeholder_hours') : t('placeholder_mileage')}
                      value={formData.mileage}
                      onChange={(e) => setFormData({...formData, mileage: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="fuel">{t('label_fuel')}</Label>
                    <Select value={formData.fuel} onValueChange={(value) => setFormData({...formData, fuel: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('label_fuel')} />
                      </SelectTrigger>
                      <SelectContent>
                        {fuelTypes.map((fuel) => (
                          <SelectItem key={fuel} value={fuel}>{t(`fuel_types.${fuel}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="transmission">{t('label_transmission')}</Label>
                    <Select value={formData.transmission} onValueChange={(value) => setFormData({...formData, transmission: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('label_transmission')} />
                      </SelectTrigger>
                      <SelectContent>
                        {transmissions.map((transmission) => (
                          <SelectItem key={transmission} value={transmission}>{t(`transmissions.${transmission}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="condition">{t('label_condition')}</Label>
                    <Select value={formData.condition} onValueChange={(value) => setFormData({...formData, condition: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('label_condition')} />
                      </SelectTrigger>
                      <SelectContent>
                        {conditions.map((condition) => (
                          <SelectItem key={condition} value={condition}>{t(`conditions.${condition}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="price">{t('label_price')}</Label>
                    <Input
                      id="price"
                      type="number"
                      placeholder={t('placeholder_price')}
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="location">{t('label_location')}</Label>
                    <Select value={formData.location} onValueChange={(value) => setFormData({...formData, location: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('label_location')} />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((location) => (
                          <SelectItem key={location} value={location}>{location}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="description">{t('label_description')}</Label>
                    <Textarea
                      id="description"
                      rows={4}
                      placeholder={
                        formData.vehicleType === "cars" ? t('placeholder_description_car') :
                        formData.vehicleType === "trucks" ? t('placeholder_description_truck') :
                        formData.vehicleType === "motorcycles" ? t('placeholder_description_motorcycle') :
                        formData.vehicleType === "equipment" ? t('placeholder_description_equipment') :
                        t('placeholder_description_boat')
                      }
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                    />
                  </div>
                </div>

                {/* Demo photo upload area */}
                <div className="space-y-2">
                  <Label>{t('label_photos')}</Label>
                  <div className="bg-primary/5 p-4 rounded-lg border border-primary/20 mb-4">
                    <p className="text-sm text-muted-foreground mb-2">
                      {t('photo_demo_title')} {t('photo_demo_desc')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('photo_full_version')}
                    </p>
                  </div>
                  <DemoImageUpload 
                    onImagesChange={(imageUrls) => setFormData({...formData, images: imageUrls})}
                    maxImages={5}
                    maxSizeMB={2}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Preview Section */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="text-primary" />
                    {t('preview_title')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Car listing preview */}
                  <div className="space-y-4">
                    {formData.images.length > 0 ? (
                      <div className="aspect-video bg-gradient-to-br from-muted to-muted-foreground/20 rounded-lg overflow-hidden relative">
                        <img 
                          src={formData.images[0]} 
                          alt="Prima imagine încărcată" 
                          className="w-full h-full object-cover"
                        />
                        <Badge className="absolute top-2 right-2 bg-primary text-primary-foreground">
                          {t('badge_promoted')}
                        </Badge>
                        {formData.images.length > 1 && (
                          <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded-sm text-sm">
                            +{formData.images.length - 1} {country === 'UK' ? 'photos' : country === 'DE' ? 'Fotos' : 'fotografii'}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="aspect-video bg-gradient-to-br from-muted to-muted-foreground/20 rounded-lg flex items-center justify-center relative overflow-hidden">
                        <Camera className="text-muted-foreground" size={48} />
                        <Badge className="absolute top-2 right-2 bg-primary text-primary-foreground">
                          {t('badge_promoted')}
                        </Badge>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-xl font-semibold">{preview.title}</h3>
                          <p className="text-muted-foreground">{preview.subtitle}</p>
                          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                            📍 {preview.location}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-primary">{preview.price}</p>
                          <Badge variant="outline" className="mt-1">{preview.condition}</Badge>
                        </div>
                      </div>
                      
                      <div className="pt-2">
                        <p className="text-sm text-muted-foreground line-clamp-3">{preview.description}</p>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 pt-2">
                        <Badge>{t('badge_featured')}</Badge>
                        <Badge variant="secondary">{t('badge_first_week')}</Badge>
                        <Badge variant="outline">{t('badge_advanced_stats')}</Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Action buttons */}
              <div className="space-y-4">
                <Button onClick={handleDemoSubmit} size="lg" className="w-full">
                  <Sparkles className="mr-2" size={18} />
                  {t('btn_complete_demo')}
                </Button>
                
                <Card className="bg-primary/5 border-primary/20">
                  <CardContent className="p-4">
                    <div className="text-center space-y-3">
                      <h4 className="font-medium">{t('liked_title')}</h4>
                      <p className="text-sm text-muted-foreground">
                        {t('liked_description')}
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button asChild size="sm" className="flex-1">
                          <Link to={config.path === '/' ? '/pricing' : `${config.path}/pricing`}>
                            {t('btn_view_plans')}
                            <ArrowRight className="ml-2" size={16} />
                          </Link>
                        </Button>
                        <Button variant="outline" asChild size="sm" className="flex-1">
                          <Link to="/create-listing-v2">{t('btn_create_real_listing')}</Link>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DemoGratuit;